import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CircleNotch, ArrowCounterClockwise, Database, Broom, Tag } from "@phosphor-icons/react";

function fmtWhen(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-US", {
    timeZone: "America/New_York", month: "2-digit", day: "2-digit", year: "2-digit",
    hour: "numeric", minute: "2-digit",
  }) + " ET";
}

async function loadMigrationData(setHubspot, setOutcomes, setTaxonomy, setBackups) {
  try {
    const [h, o, t, b] = await Promise.all([
      api.get("/admin/migrations/hubspot-ownership/preview"),
      api.get("/admin/migrations/activity-outcomes/preview"),
      api.get("/admin/migrations/taxonomy/preview"),
      api.get("/admin/migrations/backups"),
    ]);
    setHubspot(h.data); setOutcomes(o.data); setTaxonomy(t.data); setBackups(b.data);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
  }
}

function SectionCard({ icon: Icon, title, description, children }) {
  return (
    <div className="rounded-sm border border-[#E4E4E7] bg-white p-5">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-[#032852]/8 text-[#032852]">
          <Icon size={20} weight="bold" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-bold text-[#032852]">{title}</h3>
          <p className="mt-0.5 text-xs text-[#6D6D70]">{description}</p>
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </div>
  );
}

const TH = "px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wide text-[#6D6D70]";
const TD = "px-3 py-2 text-xs text-[#52525B]";

export function MigrationsTab() {
  const [hubspot, setHubspot] = useState(null);
  const [outcomes, setOutcomes] = useState(null);
  const [taxonomy, setTaxonomy] = useState(null);
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(null); // 'hubspot' | 'outcomes' | 'taxonomy' | backup id

  const helpers = useRef({ loadMigrationData });
  const load = useCallback(() => helpers.current.loadMigrationData(setHubspot, setOutcomes, setTaxonomy, setBackups),
    [helpers, setHubspot, setOutcomes, setTaxonomy, setBackups]);
  useEffect(() => { load(); }, [load]);

  const run = async (key, confirmText, request, doneMsg) => {
    if (!window.confirm(confirmText)) return;
    setBusy(key);
    try {
      const { data } = await request();
      toast.success(data.message || doneMsg(data));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const executeHubspot = () => run("hubspot",
    `Reassign ${hubspot.matched} HubSpot-imported prospect(s) to Shirley Garcia with lead source Cold Call?\n\nA full backup is taken first and the run can be rolled back.`,
    () => api.post("/admin/migrations/hubspot-ownership/execute"),
    (d) => `${d.updated} prospect(s) migrated — backup saved`);

  const executeOutcomes = () => run("outcomes",
    `Normalize ${outcomes.to_update} activity outcome(s) to the canonical list?\n\nOriginal text is preserved on each record and a backup is taken first.`,
    () => api.post("/admin/migrations/activity-outcomes/execute"),
    (d) => `${d.updated} outcome(s) normalized — backup saved`);

  const executeTaxonomy = () => run("taxonomy",
    `Standardize market segments and occupancy options?\n\n${taxonomy.legacy_found} legacy option(s) are retired and ${taxonomy.properties_to_remap} propert(ies) are remapped to the new values. A full backup is taken first and the run can be rolled back.`,
    () => api.post("/admin/migrations/taxonomy/execute"),
    (d) => `${d.legacy_removed} legacy option(s) retired, ${d.updated} propert(ies) remapped — backup saved`);

  const rollback = (b) => run(b.id,
    `Roll back "${b.label}"?\n\nThe ${b.record_count} affected record(s) are restored to their pre-migration values.`,
    () => api.post(`/admin/migrations/backups/${b.id}/rollback`),
    (d) => `${d.restored} record(s) restored`);

  if (!hubspot || !outcomes || !taxonomy) {
    return <div className="flex justify-center py-16"><CircleNotch size={24} className="animate-spin text-[#032852]" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="migrations-tab">
      <SectionCard icon={Database} title="Migrated Lead Ownership Correction"
        description={'Prospects imported with the "HubSpot Migration" lead source are reassigned to Shirley Garcia (owner and original creator) with lead source Cold Call and a blank detail. Idempotent — a second run finds nothing to change.'}>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-semibold text-[#09090B]" data-testid="hubspot-matched">
            {hubspot.source_found
              ? `${hubspot.matched} prospect(s) match`
              : "No \"HubSpot Migration\" lead source exists in this environment — nothing to migrate."}
          </p>
          <Button size="sm" disabled={busy === "hubspot" || hubspot.matched === 0} onClick={executeHubspot}
            className="rounded-sm bg-[#032852] text-xs font-semibold text-white hover:bg-[#021d3d]"
            data-testid="hubspot-execute-btn">
            {busy === "hubspot" ? <CircleNotch size={14} className="animate-spin" /> : "Back up & migrate"}
          </Button>
        </div>
        {hubspot.records.length > 0 && (
          <div className="mt-3 max-h-56 overflow-auto rounded-sm border border-[#E4E4E7]">
            <table className="w-full">
              <thead className="sticky top-0 bg-[#F4F4F5]">
                <tr>{["Organization", "Current owner", "Source detail", "Original creator"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {hubspot.records.map((r) => (
                  <tr key={r.id} className="border-t border-[#F4F4F5]" data-testid={`hubspot-row-${r.id}`}>
                    <td className={`${TD} font-semibold text-[#09090B]`}>{r.organization_name}</td>
                    <td className={TD}>{r.current_owner}</td>
                    <td className={TD}>{r.lead_source_detail || "—"}</td>
                    <td className={TD}>{r.current_creator}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard icon={Broom} title="Activity Outcome Repair"
        description="Historical free-text outcomes are normalized to the canonical list (Email sent, Left voicemail, Connected, …). Records whose outcome cannot be determined are set to “No outcome recorded” for manual correction; the original text is preserved on every changed record.">
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-semibold text-[#09090B]" data-testid="outcomes-to-update">
            {outcomes.to_update} of {outcomes.total_activities} activit(ies) need repair
          </p>
          <Button size="sm" disabled={busy === "outcomes" || outcomes.to_update === 0} onClick={executeOutcomes}
            className="rounded-sm bg-[#032852] text-xs font-semibold text-white hover:bg-[#021d3d]"
            data-testid="outcomes-execute-btn">
            {busy === "outcomes" ? <CircleNotch size={14} className="animate-spin" /> : "Back up & repair"}
          </Button>
        </div>
        {outcomes.summary.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {outcomes.summary.map((s) => (
              <Badge key={s.outcome} className="rounded-sm border-0 bg-[#F4F4F5] text-[11px] font-semibold text-[#52525B] hover:bg-[#F4F4F5]">
                {s.outcome}: {s.count}
              </Badge>
            ))}
          </div>
        )}
        {outcomes.records.length > 0 && (
          <div className="mt-3 max-h-56 overflow-auto rounded-sm border border-[#E4E4E7]">
            <table className="w-full">
              <thead className="sticky top-0 bg-[#F4F4F5]">
                <tr>{["Date", "Current outcome", "Becomes"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {outcomes.records.map((r) => (
                  <tr key={r.activity_id} className="border-t border-[#F4F4F5]">
                    <td className={TD}>{r.date}</td>
                    <td className={`${TD} max-w-[280px] truncate`}>{r.from}</td>
                    <td className={`${TD} font-semibold text-[#032852]`}>{r.to}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      <SectionCard icon={Tag} title="Market Segment & Occupancy Standardization"
        description={"Legacy values are remapped to the standardized lists (Commercial → Commercial & Office, Financial → Financial Institutions, Single-Tenant → Single-Tenant Leased, …) and retired from every dropdown, filter and export. Idempotent — a second run finds nothing to change."}>
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm font-semibold text-[#09090B]" data-testid="taxonomy-matched">
            {taxonomy.legacy_found === 0
              ? "All options already match the standardized lists — nothing to migrate."
              : `${taxonomy.legacy_found} legacy option(s) found · ${taxonomy.properties_to_remap} propert(ies) to remap`}
          </p>
          <Button size="sm" disabled={busy === "taxonomy" || taxonomy.legacy_found === 0} onClick={executeTaxonomy}
            className="rounded-sm bg-[#032852] text-xs font-semibold text-white hover:bg-[#021d3d]"
            data-testid="taxonomy-execute-btn">
            {busy === "taxonomy" ? <CircleNotch size={14} className="animate-spin" /> : "Back up & migrate"}
          </Button>
        </div>
        {taxonomy.mappings.length > 0 && (
          <div className="mt-3 max-h-56 overflow-auto rounded-sm border border-[#E4E4E7]">
            <table className="w-full">
              <thead className="sticky top-0 bg-[#F4F4F5]">
                <tr>{["Type", "Legacy value", "Becomes", "Properties affected"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr>
              </thead>
              <tbody data-testid="taxonomy-mapping-table">
                {taxonomy.mappings.map((m) => (
                  <tr key={`${m.kind}-${m.from}`} className="border-t border-[#F4F4F5]">
                    <td className={TD}>{m.kind}</td>
                    <td className={`${TD} font-semibold text-[#09090B]`}>{m.from}</td>
                    <td className={`${TD} font-semibold text-[#032852]`}>{m.to}</td>
                    <td className={TD}>{m.property_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#6D6D70]">Standardized market segments</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {taxonomy.segments_target.map((n) => (
                <Badge key={n} className="rounded-sm border-0 bg-[#032852]/5 text-[10px] font-semibold text-[#032852] hover:bg-[#032852]/5">{n}</Badge>
              ))}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wide text-[#6D6D70]">Standardized occupancy options</p>
            <div className="mt-1 flex flex-wrap gap-1">
              {taxonomy.occupancy_target.map((n) => (
                <Badge key={n} className="rounded-sm border-0 bg-[#569A1F]/10 text-[10px] font-semibold text-[#457d18] hover:bg-[#569A1F]/10">{n}</Badge>
              ))}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard icon={ArrowCounterClockwise} title="Migration Backups & Rollback"
        description="Every execution stores a full pre-migration backup with an audit-trail entry. Rolling back restores the affected records exactly as they were.">
        {backups.length === 0 ? (
          <p className="text-xs text-[#6D6D70]" data-testid="backups-empty">No migrations have been executed yet.</p>
        ) : (
          <div className="divide-y divide-[#F4F4F5] rounded-sm border border-[#E4E4E7]" data-testid="backups-list">
            {backups.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5" data-testid={`backup-row-${b.id}`}>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#09090B]">{b.label}</p>
                  <p className="text-[11px] text-[#6D6D70]">{fmtWhen(b.created_at)} · {b.record_count} record(s)</p>
                </div>
                {b.restored ? (
                  <Badge className="rounded-sm border-0 bg-[#6D6D70]/10 text-[11px] font-semibold text-[#6D6D70] hover:bg-[#6D6D70]/10">
                    Rolled back
                  </Badge>
                ) : (
                  <Button size="sm" variant="outline" disabled={busy === b.id} onClick={() => rollback(b)}
                    className="rounded-sm border-[#DC2626]/40 text-xs font-semibold text-[#B91C1C] hover:bg-[#DC2626]/5"
                    data-testid={`rollback-btn-${b.id}`}>
                    {busy === b.id ? <CircleNotch size={14} className="animate-spin" /> : "Roll back"}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
